import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import type { ApiEnvelope, PageResponse } from "@/lib/api/types";

export const PAGE_SIZE = 6;

interface PagedQueryArgs<T, Extra extends object> {
  /** TanStack Query key prefix (e.g. ["users"], ["merchants"]). */
  key: readonly unknown[];
  /** Axios call — receives the assembled query params, returns the envelope. */
  fetcher: (
    args: { q: string; page: number; size: number } & Extra,
  ) => Promise<AxiosResponse<ApiEnvelope<PageResponse<T>>>>;
  /** Optional extra params that the underlying endpoint accepts. */
  extra?: Extra;
  /** Debounce window for the search query in ms. */
  debounceMs?: number;
}

/**
 * Shared hook for every paged list page:
 *   - Owns `q` (search) + `page` state
 *   - Debounces `q` before re-fetching
 *   - Resets to page 0 whenever the search query changes
 *   - Returns the TanStack Query result + page handlers
 *   - Keeps previous data so the table doesn't flicker between pages
 */
export function usePagedQuery<T, Extra extends object = object>({
  key, fetcher, extra, debounceMs = 250,
}: PagedQueryArgs<T, Extra>) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQ(q.trim()), debounceMs);
    return () => window.clearTimeout(t);
  }, [q, debounceMs]);

  // Any time the *effective* search query changes, snap back to page 0
  // so the user isn't stranded on page 7 of a filter that only has 2.
  useEffect(() => {
    setPage(0);
  }, [debouncedQ, JSON.stringify(extra ?? {})]);

  const query = useQuery({
    queryKey: [...key, debouncedQ, page, extra ?? {}],
    queryFn: () =>
      fetcher({
        q: debouncedQ,
        page,
        size: PAGE_SIZE,
        ...(extra ?? ({} as Extra)),
      }).then((r) => r.data.data),
    placeholderData: keepPreviousData,
  });

  return {
    q,
    setQ,
    page,
    setPage,
    items: query.data?.items ?? [],
    totalItems: query.data?.totalItems ?? 0,
    totalPages: query.data?.totalPages ?? 0,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
  };
}
